<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ResellerAPI from 'dashboard/api/reseller';
import Button from 'dashboard/components-next/button/Button.vue';
const props = defineProps({ account: { type: Object, required: true } });
const { t } = useI18n();
const detail = ref(null);
const busy = ref(false);
const error = ref(false);
const page = ref(1);
const money = (value, currency = 'BRL') =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(value);
const date = value =>
  value
    ? new Date(
        value.length === 10 ? `${value}T12:00:00` : value
      ).toLocaleDateString('pt-BR')
    : '—';
const load = async next => {
  busy.value = true;
  error.value = false;
  try {
    const { data } = await ResellerAPI.contractDetails(props.account.id, next);
    detail.value = data;
    page.value = next;
  } catch {
    error.value = true;
  } finally {
    busy.value = false;
  }
};
onMounted(() => load(1));
const components = [
  'base_plan_price',
  'ai_agents_charge',
  'resources_charge',
  'additional_charges_total',
  'account_addons_charge',
  'discount_applied',
  'total',
];
</script>

<template>
  <section class="space-y-5 p-4 text-sm text-n-slate-12">
    <p v-if="busy">{{ t('REVENDA.SUB_ACCOUNTS.LOADING') }}</p>
    <Button v-if="error" :label="t('PORTFOLIO.RETRY')" @click="load(page)" />
    <template v-if="detail">
      <div class="rounded-lg border border-n-weak p-3">
        <h3 class="mb-2 font-semibold">{{ t('PORTFOLIO.REFERRAL') }}</h3>
        <p>{{ detail.referral.path.map(item => item.name).join(' → ') }}</p>
        <p>
          {{ t('PORTFOLIO.INDICATOR') }}:
          {{ detail.referral.indicator?.name || t('PORTFOLIO.NO_INDICATOR') }}
        </p>
        <p>
          {{ t('PORTFOLIO.PARENT') }}: {{ detail.referral.parent?.name || '—' }}
        </p>
        <p v-if="detail.referral.mismatch" class="mt-2 text-n-amber-10">
          {{ t('PORTFOLIO.REFERRAL_REVIEW') }}
        </p>
      </div>
      <p class="text-n-slate-11">{{ t('PORTFOLIO.CONTRACT_HELP') }}</p>
      <template v-if="detail.contract">
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div>
            <p class="text-n-slate-10">{{ t('PORTFOLIO.PLAN') }}</p>
            {{ detail.contract.plan }}
          </div>
          <div>
            <p class="text-n-slate-10">{{ t('PORTFOLIO.MODE') }}</p>
            {{ t(`PORTFOLIO.${detail.contract.pricing_mode}`) }}
          </div>
          <div>
            <p class="text-n-slate-10">{{ t('PORTFOLIO.START') }}</p>
            {{ date(detail.contract.started_at) }}
          </div>
          <div>
            <p class="text-n-slate-10">{{ t('PORTFOLIO.CYCLE') }}</p>
            {{ t(`PORTFOLIO.${detail.contract.billing_cycle}`) }}
          </div>
        </div>
        <div class="grid gap-4 lg:grid-cols-2">
          <div class="rounded-lg border border-n-weak p-3">
            <h3 class="mb-3 font-semibold">{{ t('PORTFOLIO.CONTRACTED') }}</h3>
            <div
              v-for="(quantity, key) in detail.contract.quantities"
              :key="key"
              class="flex justify-between gap-3 py-1"
            >
              <span>
                {{ t(`PORTFOLIO.${key}`) }}
              </span>
              <span>
                {{ quantity }}
              </span>
            </div>
            <p class="mt-3 text-xs text-n-slate-10">
              {{
                t('PORTFOLIO.USAGE', {
                  users: account.users_count,
                  inboxes: account.inboxes_count,
                })
              }}
            </p>
          </div>
          <div class="rounded-lg border border-n-weak p-3">
            <h3 class="mb-3 font-semibold">{{ t('PORTFOLIO.COMPOSITION') }}</h3>
            <div
              v-for="key in components"
              :key="key"
              class="flex justify-between gap-3 py-1"
              :class="key === 'total' ? 'font-semibold' : ''"
            >
              <span>
                {{ t(`PORTFOLIO.${key}`) }}
              </span>
              <span>
                {{ money(detail.contract.calculation[key]) }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="detail.contract.calculation.negotiated_package"
          class="rounded-lg border border-n-weak p-3"
        >
          <h3 class="mb-2 font-semibold">
            {{ t('PORTFOLIO.PACKAGE_INCLUDED') }}
          </h3>
          <p class="text-xs text-n-slate-10">
            {{ t('PORTFOLIO.CATALOG_HELP') }}
          </p>
          <div
            v-for="group in ['resources', 'lia']"
            :key="group"
            class="grid grid-cols-2 gap-2"
          >
            <template
              v-for="(value, key) in detail.contract.calculation
                .negotiated_package[group]"
              :key="key"
            >
              <span>
                {{ t(`PORTFOLIO.${key}`) }}
              </span>
              <span>
                {{ value }}
              </span>
            </template>
          </div>
          <div
            v-for="item in detail.contract.package_addons"
            :key="item.name"
            class="flex justify-between gap-3 py-1"
          >
            <span>
              {{ item.name }}
            </span>
            <span>
              {{ item.quantity }}
            </span>
          </div>
          <div
            v-for="(amount, name) in detail.contract.calculation
              .negotiated_package.additional"
            :key="name"
            class="flex justify-between gap-3 py-1"
          >
            <span>
              {{ name }}
            </span>
            <span>
              {{ money(amount) }}
            </span>
          </div>
          <p class="mt-2 text-xs text-n-slate-10">
            {{
              date(detail.contract.calculation.negotiated_package.captured_at)
            }}
          </p>
        </div>
        <div>
          <h3 class="mb-2 font-semibold">{{ t('PORTFOLIO.ACCESS') }}</h3>
          <div
            v-for="(module, key) in detail.contract.module_access"
            :key="key"
            class="flex justify-between gap-3 border-b border-n-weak py-2"
          >
            <span>
              {{ module.label }}
            </span>
            <span>
              {{
                t(module.enabled ? 'PORTFOLIO.ENABLED' : 'PORTFOLIO.DISABLED')
              }}
              · {{ t(`PORTFOLIO.${module.source}`) }}
            </span>
          </div>
        </div>
        <div v-if="detail.contract.modules.length">
          <h3 class="mb-2 font-semibold">{{ t('PORTFOLIO.MODULES') }}</h3>
          <div
            v-for="(module, index) in detail.contract.modules"
            :key="index"
            class="flex flex-wrap justify-between gap-2 border-b border-n-weak py-2"
          >
            <span> {{ module.name }} · {{ module.quantity }} </span>
            <span>
              {{ money(module.amount) }} ·
              {{ t(`PORTFOLIO.${module.status}`) }} ·
              {{ date(module.started_at) }}
            </span>
          </div>
        </div>
        <p class="text-xs text-n-slate-10">{{ t('PORTFOLIO.CATALOG_HELP') }}</p>
        <div
          v-for="group in [
            'additional_charges',
            'ai_agents_breakdown',
            'account_addons',
          ]"
          :key="group"
        >
          <div
            v-for="(row, index) in detail.contract.calculation[group] || []"
            :key="index"
            class="flex justify-between gap-3 py-1"
          >
            <span>
              {{ row.name || row.description }} ·
              {{ row.count ?? row.quantity ?? '' }}
              <span v-if="row.included_quantity != null" class="block text-xs">
                {{
                  t('PORTFOLIO.INCLUDED_COUNT', {
                    count: row.included_quantity,
                  })
                }}
              </span>
            </span>
            <span>
              {{ money(row.amount ?? row.total) }}
            </span>
          </div>
        </div>
      </template>
      <p v-else>{{ t('PORTFOLIO.NO_CONTRACT') }}</p>
      <div class="grid gap-3 md:grid-cols-2">
        <div>
          <h3 class="font-semibold">{{ t('PORTFOLIO.LAST_PAYMENT') }}</h3>
          <p v-if="detail.last_subscription_payment">
            {{ money(detail.last_subscription_payment.total_amount) }} ·
            {{ date(detail.last_subscription_payment.paid_at) }}
          </p>
          <p v-if="detail.last_subscription_payment">
            {{ date(detail.last_subscription_payment.billing_period_start) }} —
            {{ date(detail.last_subscription_payment.billing_period_end) }}
          </p>
          <p v-else>—</p>
        </div>
        <div>
          <h3 class="font-semibold">{{ t('PORTFOLIO.NEXT_INVOICE') }}</h3>
          <p v-if="detail.next_subscription_invoice">
            {{ money(detail.next_subscription_invoice.total_amount) }} ·
            {{ date(detail.next_subscription_invoice.due_date) }}
          </p>
          <p v-else>—</p>
        </div>
      </div>
      <h3 class="font-semibold">{{ t('PORTFOLIO.HISTORY') }}</h3>
      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead>
            <tr>
              <th>{{ t('PORTFOLIO.TYPE') }}</th>
              <th>{{ t('PORTFOLIO.PERIOD') }}</th>
              <th>{{ t('PORTFOLIO.PAYMENT') }}</th>
              <th>{{ t('PORTFOLIO.STATUS') }}</th>
              <th>{{ t('PORTFOLIO.total') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invoice in detail.invoices"
              :key="invoice.id"
              class="border-b border-n-weak"
            >
              <td class="py-3">
                <details>
                  <summary class="cursor-pointer">
                    {{ t(`PORTFOLIO.${invoice.invoice_type}`) }} #{{
                      invoice.id
                    }}
                  </summary>
                  <div class="min-w-56 py-2">
                    <p v-if="invoice.breakdown?.description">
                      {{ invoice.breakdown.description }}
                    </p>
                    <template v-for="key in components" :key="key">
                      <p v-if="invoice.breakdown?.[key] != null">
                        {{ t(`PORTFOLIO.${key}`) }}:
                        {{ money(invoice.breakdown[key], invoice.currency) }}
                      </p>
                    </template>
                    <p>
                      {{ t('PORTFOLIO.base_plan_price') }}:
                      {{ money(invoice.base_plan_amount, invoice.currency) }}
                    </p>
                    <p>
                      {{ t('PORTFOLIO.ai_agents_charge') }}:
                      {{ money(invoice.ai_agents_amount, invoice.currency) }}
                    </p>
                  </div>
                </details>
              </td>
              <td>
                {{ date(invoice.billing_period_start) }} —
                {{ date(invoice.billing_period_end) }}
              </td>
              <td>
                {{ invoice.payment_method || '—' }} ·
                {{ date(invoice.paid_at || invoice.due_date) }}
              </td>
              <td>{{ t(`PORTFOLIO.${invoice.status}`) }}</td>
              <td>{{ money(invoice.total_amount, invoice.currency) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center gap-3">
        <Button
          :label="t('PORTFOLIO.PREVIOUS')"
          :disabled="page === 1 || busy"
          @click="load(page - 1)"
        />
        <span>
          {{ page }}
        </span>
        <Button
          :label="t('PORTFOLIO.NEXT')"
          :disabled="page * 20 >= detail.invoices_count || busy"
          @click="load(page + 1)"
        />
      </div>
      <p class="text-xs text-n-slate-10">{{ t('PORTFOLIO.HISTORY_HELP') }}</p>
      <div v-if="detail.commissions.length">
        <h3 class="mb-2 font-semibold">{{ t('PORTFOLIO.COMMISSIONS') }}</h3>
        <div
          v-for="entry in detail.commissions"
          :key="entry.id"
          class="flex flex-wrap justify-between gap-2 border-b border-n-weak py-2"
        >
          <span>
            {{ entry.beneficiary }} ·
            {{ t('REVENDA.COMMISSIONS.LEVEL_N', { level: entry.level }) }} · #{{
              entry.source_invoice_id
            }}
          </span>
          <span>
            {{ money(entry.base_amount) }} · {{ entry.rate }}% =
            {{ money(entry.amount) }} ·
            {{ t(`PORTFOLIO.${entry.status}`) }}
          </span>
        </div>
      </div>
    </template>
  </section>
</template>
